import { formatPercent, formatTenure, historyNote, RetentionResult, RetentionRow } from "models/hr-dashboard";
import { GroupNameCell, subtotalRowClass, useCollapsedDivisions } from "./group-hierarchy";

interface RetentionTablesProps {
    data: RetentionResult;
}

// Table 1: tenure and departures. Table 2: turnover within the group and its share of company turnover.
// Department view: each division is a subtotal row (can be collapsed) with its departments below it.
const RetentionTables = ({ data }: RetentionTablesProps) => {
    const { collapsed, toggle, isVisible } = useCollapsedDivisions();
    const groupLabel = data.groupBy === "division" ? "Division" : "Department";
    const hiddenTitle = `Hidden: group smaller than ${data.smallGroupMin} people`;
    const rows = data.rows.filter(isVisible);

    const nameCell = (r: RetentionRow) => <GroupNameCell group={r} collapsed={collapsed} toggle={toggle} />;
    const hidden = (colSpan: number) => <td colSpan={colSpan} className="text-center text-muted" title={hiddenTitle}>—</td>;

    // "—" instead of "0 years 0 months" when nobody left
    const departedTenure = (r: RetentionRow) => (r.departed ? formatTenure(r.avgTenureDepartedMonths) : "—");

    return (
        <div className="row g-4">
            <div className="col-xl-6">
                <h5 className="mb-2">Tenure and departures — FY{data.fiscalYear}</h5>
                <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-0">
                        <thead>
                            <tr>
                                <th>{groupLabel}</th>
                                <th className="text-end">Average tenure (departed)</th>
                                <th className="text-end">Departed</th>
                                <th className="text-end">Average tenure (active)</th>
                                <th className="text-end">Active</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={`${r.kind}-${r.name}`} className={subtotalRowClass(r)}>
                                    {nameCell(r)}
                                    {r.hidden ? hidden(4) : <>
                                        <td className="text-end">{departedTenure(r)}</td>
                                        <td className="text-end">{r.departed}</td>
                                        <td className="text-end">{formatTenure(r.avgTenureActiveMonths)}</td>
                                        <td className="text-end">{r.activeCount}</td>
                                    </>}
                                </tr>
                            ))}
                            <tr className="fw-bold">
                                <td>{data.total.name}</td>
                                <td className="text-end">{departedTenure(data.total)}</td>
                                <td className="text-end">{data.total.departed}</td>
                                <td className="text-end">{formatTenure(data.total.avgTenureActiveMonths)}</td>
                                <td className="text-end">{data.total.activeCount}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="col-xl-6">
                <h5 className="mb-2">Turnover by {groupLabel.toLowerCase()} — FY{data.fiscalYear}</h5>
                <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-0">
                        <thead>
                            <tr>
                                <th>{groupLabel}</th>
                                <th className="text-end">Turnover within {groupLabel.toLowerCase()}</th>
                                <th className="text-end">Turnover split for whole company</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={`${r.kind}-${r.name}`} className={subtotalRowClass(r)}>
                                    {nameCell(r)}
                                    {r.hidden ? hidden(2) : <>
                                        <td className="text-end">{formatPercent(r.turnover)}</td>
                                        <td className="text-end">{formatPercent(r.companySplit)}</td>
                                    </>}
                                </tr>
                            ))}
                            <tr className="fw-bold">
                                <td>{data.total.name}</td>
                                <td className="text-end">{formatPercent(data.total.turnover)}</td>
                                <td className="text-end">{formatPercent(data.total.companySplit)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="col-12">
                <p className="small text-muted mb-0">
                    Formula: {data.formulaDescription}. Split for whole company = departures of the {groupLabel.toLowerCase()} / company denominator.
                    Tenure counts from the latest hire date. Departures count in the {groupLabel.toLowerCase()} the person was in when they left.
                    {data.groupBy === "department" && " Division rows show the whole division; click the arrow to hide or show its departments."}
                    {" "}{historyNote(data.historyStart, groupLabel.toLowerCase())}
                </p>
            </div>
        </div>
    );
};

export default RetentionTables;
